import {
    CoachingEventStatus,
    FocusForPerformerFocusHistory,
} from '../../../coaching-base/general';
import {
    CellNode,
    Column,
    Row,
    RowNode,
    Sort,
    SortOrder,
} from '../grid/experimental.grid';
import {
    ColumnID,
    defaultPerformerSortDefs,
    defaultTeamSortDefs,
    FocusHistoryGrid,
} from '../model/focus-history.grid.model';
import { FocusSelectorFocusForPerformer } from '../model/focus-selector-focus-for-performer.model';
import { FocusSelectorFocusForTeam } from '../model/focus-selector-focus-for-team.model';
import { TemplateID } from '../model/focus-selector.grid.model';

const isActive = (focus: FocusForPerformerFocusHistory): boolean =>
    focus.statusId === CoachingEventStatus.ACTIVE ||
    focus.statusId === CoachingEventStatus.UPDATING ||
    focus.statusId === CoachingEventStatus.PENDING_CLOSE;

export function formatFocusesForTeam(
    focusesForTeam: FocusForPerformerFocusHistory[],
    activeFocusesForPerformer: FocusSelectorFocusForPerformer[],
): FocusSelectorFocusForTeam[] {
    return focusesForTeam.map<FocusSelectorFocusForTeam>((focus) => ({
        commitmentId: focus.commitmentId,
        directReport: `${focus.firstName ?? ''} ${focus.lastName ?? ''}`.trim(),
        goalId: focus.goalId ?? null,
        focusId: focus.focusId ?? null,
        metricId: focus.metricId ?? null,
        metric: focus.metricFullName ?? '--',
        behaviour: focus.behaviourFullName ?? '',
        behaviourId: focus.behaviourId ?? null,
        tactic: focus.subBehaviourFullName ?? '',
        tacticId: focus.subBehaviourId ?? null,
        currentImprovement:
            focus.performanceVariance != null
                ? focus.performanceVariance / 100
                : null,
        daysActive: focus.daysActive ?? null,
        hasActiveCommitment:
            activeFocusesForPerformer.find(
                (activeFocus) =>
                    activeFocus.behaviourId === focus.behaviourId &&
                    activeFocus.tacticId === focus.subBehaviourId,
            ) != null,
    }));
}

export function formatFocusesForPerformer(
    focusesForPerformer: FocusForPerformerFocusHistory[],
    activeFocusesForPerformer?: FocusSelectorFocusForPerformer[],
): FocusSelectorFocusForPerformer[] {
    return focusesForPerformer.map<FocusSelectorFocusForPerformer>((focus) => ({
        commitmentId: focus.commitmentId,
        goalId: focus.goalId ?? null,
        focusId: focus.focusId ?? null,
        metricId: focus.metricId ?? null,
        metric: focus.metricFullName ?? '--',
        behaviour: focus.behaviourFullName ?? '',
        behaviourId: focus.behaviourId ?? null,
        tactic: focus.subBehaviourFullName ?? '',
        tacticId: focus.subBehaviourId ?? null,
        currentImprovement:
            focus.performanceVariance != null
                ? focus.performanceVariance / 100
                : null,
        daysActive: focus.daysActive ?? null,
        hasActiveCommitment:
            activeFocusesForPerformer?.some(
                (activeFocus) =>
                    activeFocus.behaviourId === focus.behaviourId &&
                    activeFocus.tacticId === focus.subBehaviourId,
            ) ?? isActive(focus),
    }));
}

export function makeTeamColumns(): Column[] {
    const teamCols = makePerformerColumns();
    teamCols.unshift({
        id: ColumnID.DIRECT_REPORT,
        compareFn: compare,
        ctx: { templateId: TemplateID.HEADER },
        minWidth: 135,
    });
    return teamCols;
}

export function makePerformerColumns(): Column[] {
    return [
        {
            id: ColumnID.METRIC,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 175,
        },
        {
            id: ColumnID.BEHAVIOUR,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 175,
        },
        {
            id: ColumnID.TACTIC,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 175,
        },
        {
            id: ColumnID.CURRENT_IMPROVEMENT,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 120,
        },
        {
            id: ColumnID.DAYS_ACTIVE,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 80,
        },
    ];
}

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    const focusA: FocusSelectorFocusForPerformer | FocusSelectorFocusForTeam =
        rowA.context.ctx.focus;
    const focusB: FocusSelectorFocusForPerformer | FocusSelectorFocusForTeam =
        rowB.context.ctx.focus;
    let valA: number | string;
    let valB: number | string;

    switch (def.columnId) {
        case ColumnID.DIRECT_REPORT:
            valA = (focusA as FocusSelectorFocusForTeam).directReport ?? '';
            valB = (focusB as FocusSelectorFocusForTeam).directReport ?? '';
            break;
        case ColumnID.DAYS_ACTIVE:
            valA =
                focusA.daysActive != null
                    ? focusA.daysActive
                    : Number.MIN_SAFE_INTEGER;
            valB =
                focusB.daysActive != null
                    ? focusB.daysActive
                    : Number.MIN_SAFE_INTEGER;
            break;
        default:
            return Number.MIN_SAFE_INTEGER;
    }

    if (typeof valA === 'string' && typeof valB === 'string') {
        return def.order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
        return def.order === SortOrder.ASC ? valA - valB : valB - valA;
    }

    return Number.MIN_SAFE_INTEGER;
}

export function makeTeamRows(
    focusesForTeam: FocusSelectorFocusForTeam[] = [],
    selectedRow?: Row['id'],
): FocusHistoryGrid<FocusSelectorFocusForTeam>['rows'] {
    return focusesForTeam.map((focus) => {
        const {
            directReport,
            commitmentId,
            goalId,
            metric,
            behaviour,
            tactic,
            currentImprovement,
            daysActive,
        } = focus;

        const id =
            goalId != null
                ? `${commitmentId}|${goalId.toString()}`
                : `${commitmentId}|${behaviour ?? ''}|${tactic ?? ''}`;

        const checked =
            selectedRow != null &&
            (goalId == null
                ? `${commitmentId}|${behaviour ?? ''}|${tactic ?? ''}` ===
                  selectedRow
                : selectedRow === `${commitmentId}|${goalId.toString()}`);

        return {
            id,
            ctx: {
                templateId: TemplateID.CONTENT,
                focus,
            },
            cells: [
                {
                    columnId: ColumnID.DIRECT_REPORT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        directReport,
                    },
                },
                {
                    columnId: ColumnID.METRIC,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        metric,
                    },
                },
                {
                    columnId: ColumnID.BEHAVIOUR,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        behaviour,
                    },
                },
                {
                    columnId: ColumnID.TACTIC,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        tactic,
                    },
                },
                {
                    columnId: ColumnID.CURRENT_IMPROVEMENT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        currentImprovement,
                    },
                },
                {
                    columnId: ColumnID.DAYS_ACTIVE,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        daysActive,
                    },
                },
            ],
        };
    });
}

export function makePerformerRows(
    focusesForPerformer: FocusSelectorFocusForPerformer[] = [],
    selectedRow?: Row['id'],
): FocusHistoryGrid<FocusSelectorFocusForPerformer>['rows'] {
    return focusesForPerformer.map((focus) => {
        const {
            commitmentId,
            goalId,
            metric,
            behaviour,
            tactic,
            currentImprovement,
            daysActive,
        } = focus;

        const id =
            goalId != null
                ? `${commitmentId}|${goalId.toString() ?? ''}`
                : `${commitmentId}-${behaviour ?? ''}-${tactic ?? ''}`;
        const checked =
            selectedRow != null &&
            (goalId == null ? tactic === selectedRow : selectedRow === id);

        return {
            id,
            ctx: {
                templateId: TemplateID.CONTENT,
                focus,
            },
            cells: [
                {
                    columnId: ColumnID.METRIC,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        metric,
                    },
                },
                {
                    columnId: ColumnID.BEHAVIOUR,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        behaviour,
                    },
                },
                {
                    columnId: ColumnID.TACTIC,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        tactic,
                    },
                },
                {
                    columnId: ColumnID.CURRENT_IMPROVEMENT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        currentImprovement,
                    },
                },
                {
                    columnId: ColumnID.DAYS_ACTIVE,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked,
                        daysActive,
                    },
                },
            ],
        };
    });
}

export function makeGrid(
    gridType: 'performer' | 'team',
    focuses: FocusSelectorFocusForPerformer[] | FocusSelectorFocusForTeam[],
): FocusHistoryGrid<
    FocusSelectorFocusForPerformer | FocusSelectorFocusForTeam
> {
    const columns =
        gridType === 'performer' ? makePerformerColumns() : makeTeamColumns();
    const rows =
        gridType === 'performer'
            ? makePerformerRows(focuses)
            : makeTeamRows(focuses as FocusSelectorFocusForTeam[]);

    return {
        columns,
        rows,
        sortDefs:
            gridType === 'performer'
                ? defaultPerformerSortDefs
                : defaultTeamSortDefs,
        defaultSortDefs:
            gridType === 'performer'
                ? defaultPerformerSortDefs
                : defaultTeamSortDefs,
        gridRowsTop: [],
        rendererOpts: {
            columns,
            defaultRenderFn: null,
        },
    };
}

export function getSelectedFocusFromRows<
    ModelType extends
        | FocusSelectorFocusForPerformer
        | FocusSelectorFocusForTeam,
>(
    rows: FocusHistoryGrid<ModelType>['rows'],
): FocusHistoryGrid<ModelType>['selection'] {
    let selection: FocusHistoryGrid<ModelType>['selection'] = null;

    for (const row of rows) {
        if (row.cells.length > 0 && row.cells[0].ctx?.checked === true) {
            selection = {
                behaviour: row.ctx?.focus?.behaviour ?? '',
                behaviourId: row.ctx?.focus?.behaviourId ?? null,
                tactic: row.ctx?.focus?.tactic ?? '',
                tacticId: row.ctx?.focus?.tacticId ?? null,
            };
            break;
        }
    }

    return selection;
}
